import { useState, useEffect, useRef } from 'react';

// ── Tokens ────────────────────────────────────────────────────────
const C = {
  purple:    '#6414E9',
  purpleDim: '#c9a8f7',
  teal:      '#0AEDC7',
  bg:        '#f5f3ff',
  card:      '#ffffff',
  border:    '#e0e0e0',
  text:      '#1a0a3c',
  textSub:   '#5a6a8a',
  red:       '#e05555',
  redBg:     '#fef0ef',
  redBorder: '#f5c6c6',
};

// ── Mock API ──────────────────────────────────────────────────────
const MOCK = {
  individual: { name: 'Alexandra J. Morrison', type: 'individual', email: 'a.morrison@email.com' },
  business:   { name: 'Morrison & Co. LLC',    type: 'business',  email: 'contact@morrisonco.com' },
};

const BANKS = ['Chase', 'Bank of America', 'Wells Fargo', 'Citibank', 'HSBC'];

// Simulates your real API call — replace this with fetch()
function lookupRecipient(bank, acct) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const type = parseInt(acct.slice(-1)) % 2 === 0 ? 'business' : 'individual';
      resolve(MOCK[type]);
    }, 1400);
  });
}

// ── Main Component ────────────────────────────────────────────────
export default function AddRecipient({ onSubmit }) {
  const [accountType, setAccountType]     = useState('individual');
  const [bank, setBank]                   = useState('');
  const [acct, setAcct]                   = useState('');
  const [bankId, setBankId]               = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [email, setEmail]                 = useState('');
  const [nameLocked, setNameLocked]       = useState(false);
  const [emailLocked, setEmailLocked]     = useState(false);
  const [acctHint, setAcctHint]           = useState({ text: '', color: '' });
  const [nameHint, setNameHint]           = useState({ text: '', color: '' });
  const [showCallout, setShowCallout]     = useState(false);
  const [confirmed, setConfirmed]         = useState(false);
  const [showRecipient, setShowRecipient] = useState(false);
  const timerRef = useRef(null);

  // Show recipient section once bank + 4 chars entered
  useEffect(() => {
    if (bank && acct.length >= 4) {
      setShowRecipient(true);
    } else {
      setShowRecipient(false);
      resetRecipient();
    }
  }, [bank, acct]);

  // Trigger lookup after 8 chars
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (bank && acct.length >= 8) {
      setAcctHint({ text: '⏳ Verifying…', color: '#7b6a99' });
      timerRef.current = setTimeout(async () => {
        const result = await lookupRecipient(bank, acct);
        applyResult(result);
      }, 600);
    } else if (acct.length < 8) {
      setAcctHint({ text: '', color: '' });
    }
    return () => clearTimeout(timerRef.current);
  }, [bank, acct]);

  function applyResult(result) {
    setAccountType(result.type);
    setRecipientName(result.name);
    setEmail(result.email);
    setNameLocked(true);
    setEmailLocked(true);
    setAcctHint({ text: '✓ Account verified', color: C.purple });
    setNameHint({ text: '⬆ Please confirm this name', color: C.purple });
    setShowCallout(true);
    setConfirmed(false);
  }

  function confirmName() {
    setShowCallout(false);
    setNameHint({ text: '✓ Name confirmed', color: C.teal });
    setConfirmed(true);
  }

  function onNameType(val) {
    setRecipientName(val);
    setNameLocked(false);
    setShowCallout(false);
    setNameHint({ text: '', color: '' });
    setConfirmed(true);
  }

  function resetRecipient() {
    setRecipientName('');
    setEmail('');
    setNameLocked(false);
    setEmailLocked(false);
    setAcctHint({ text: '', color: '' });
    setNameHint({ text: '', color: '' });
    setShowCallout(false);
    setConfirmed(false);
  }

  const canSubmit = showRecipient && recipientName && (confirmed || !nameLocked);

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit?.({ accountType, bank, acct, bankId, recipientName, email });
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', justifyContent: 'center', padding: '48px 24px', fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Serif+Display&display=swap" rel="stylesheet" />

      <div style={{ width: '100%', maxWidth: 780 }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, color: C.text, fontWeight: 400, marginBottom: 24 }}>
          Add Recipient
        </h1>

        <div style={styles.card}>

          {/* Account Type */}
          <Field label="Account Type">
            <select value={accountType} onChange={e => setAccountType(e.target.value)} style={{ ...styles.input, maxWidth: 320 }}>
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
          </Field>

          <Divider />

          {/* Bank Information */}
          <SectionTitle>Bank Information</SectionTitle>
          <div style={styles.grid3}>
            <Field label="Bank Name" required>
              <select value={bank} onChange={e => setBank(e.target.value)} style={styles.input}>
                <option value="">Select bank</option>
                {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Account Number" required>
              <input
                value={acct}
                onChange={e => setAcct(e.target.value)}
                placeholder="Enter account number"
                style={styles.input}
              />
              <Hint {...acctHint} />
            </Field>
            <Field label="Bank Identifier" optional>
              <input value={bankId} onChange={e => setBankId(e.target.value)} placeholder="BIC, IFSC, etc." style={styles.input} />
            </Field>
          </div>

          {/* Recipient Details */}
          {showRecipient && (
            <>
              <Divider />
              <SectionTitle>Recipient Details</SectionTitle>

              {/* Name + Callout */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Field label="Recipient Name" required>
                    <input
                      value={recipientName}
                      onChange={e => onNameType(e.target.value)}
                      readOnly={nameLocked}
                      style={{
                        ...styles.input,
                        borderColor: nameLocked ? (confirmed ? C.purple : C.purple) : C.border,
                        color: nameLocked ? C.textSub : C.text,
                        cursor: nameLocked ? 'not-allowed' : 'text',
                      }}
                    />
                    <Hint {...nameHint} />
                  </Field>
                </div>

                {showCallout && (
                  <div style={styles.callout}>
                    <div style={styles.calloutTitle}>
                      <span>△</span> Please verify
                    </div>
                    <div style={styles.calloutBody}>
                      This name was auto-filled from your bank records. Confirm it looks correct before continuing.
                    </div>
                    <button onClick={confirmName} style={styles.calloutBtn}>
                      Looks correct ✓
                    </button>
                  </div>
                )}
              </div>

              {/* Email */}
              <div style={{ maxWidth: 320 }}>
                <Field label="Email" optional>
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    readOnly={emailLocked}
                    placeholder="Optional"
                    style={{
                      ...styles.input,
                      color: emailLocked ? C.textSub : C.text,
                      cursor: emailLocked ? 'not-allowed' : 'text',
                    }}
                  />
                </Field>
              </div>
            </>
          )}

          {/* Submit */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 32 }}>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                background: canSubmit ? C.purple : C.purpleDim,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '13px 36px',
                fontSize: 15,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s',
              }}
            >
              Add Recipient
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────
function SectionTitle({ children }) {
  return <div style={{ fontSize: 16, fontWeight: 600, color: '#1a0a3c', marginBottom: 20 }}>{children}</div>;
}

function Divider() {
  return <hr style={{ border: 'none', borderTop: '1px solid #e8e0fa', margin: '28px 0' }} />;
}

function Field({ label, required, optional, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#5a6a8a', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        {optional && <span style={{ color: '#9aa', fontWeight: 400, textTransform: 'none', marginLeft: 4 }}>(optional)</span>}
      </label>
      {children}
    </div>
  );
}

function Hint({ text, color }) {
  return <div style={{ fontSize: 12, fontWeight: 500, marginTop: 5, height: 18, color }}>{text}</div>;
}

// ── Styles ────────────────────────────────────────────────────────
const styles = {
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '36px 40px',
    boxShadow: '0 2px 24px rgba(100,20,233,0.08)',
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 20,
  },
  input: {
    width: '100%',
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    color: '#1a0a3c',
    background: '#fff',
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
  },
  callout: {
    flexShrink: 0,
    width: 220,
    background: '#fef0ef',
    border: '1.5px solid #f5c6c6',
    borderLeft: '4px solid #e05555',
    borderRadius: 12,
    padding: '14px 16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    marginTop: 22,
    animation: 'popIn 0.2s ease',
  },
  calloutTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#d94040',
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  calloutBody: {
    fontSize: 12,
    color: '#4a4a4a',
    lineHeight: 1.5,
    marginBottom: 12,
  },
  calloutBtn: {
    background: '#fef0ef',
    color: '#e05555',
    border: '1.5px solid #f5c6c6',
    borderRadius: 7,
    padding: '8px 0',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
    width: '100%',
  },
};